import { scmpPlus } from "@product/scmp-sdk";
import type { NextPageContext, PageProps } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import type { AppBarVariant } from "~/components/app-bar/types";
import { DefaultCustomContents } from "~/components/article/article-render/consts";
import { FallbackLoading } from "~/components/fallback-loading";
import { SectionContextProvider } from "~/components/section/contexts";
import { SectionSeo } from "~/components/section/section-seo";
import { SectionStyleSeo } from "~/components/section/section-style/section-style-seo";
import { SubSection } from "~/components/section/sub-section";
import { SubSectionStyle } from "~/components/section/sub-section/sub-section-style";
import { useTrackCurrentItem } from "~/lib/current-item";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { parseQueryString } from "~/lib/utils";
import { Variant as SectionVariant, Variant } from "~/pages/section/enums";
import type { subSectionPageQuery } from "~/queries/__generated__/subSectionPageQuery.graphql";

const query = graphql`
  query subSectionPageQuery(
    $after: String
    $articlesQueueName: String!
    $commentQueueName: String!
    $customContents: [CustomContentInput]
    $entityId: String!
    $first: Int
    $isStyleSubSection: Boolean!
    $multimediaQueueName: String!
    $relatedSectionsQueueName: String!
    $scmpPlusPaywallTypeIds: [String]
    $parentSectionUuid: String!
    $relatedTopicsQueueName: String!
  ) {
    subSection: section(filter: { entityId: $entityId }) {
      ...contextsSectionContextProviderSection
      ...sectionSeoSection
      ...hooksTrackCurrentItemBase @arguments(customContents: $customContents)
    }

    ...subSectionQuery
      @arguments(
        entityId: $entityId
        articlesQueueName: $articlesQueueName
        commentQueueName: $commentQueueName
        multimediaQueueName: $multimediaQueueName
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        after: $after
        first: $first
        parentSectionUuid: $parentSectionUuid
        relatedTopicsQueueName: $relatedTopicsQueueName
      )
      @skip(if: $isStyleSubSection)

    ...subSectionStyleContentQuery
      @arguments(
        after: $after
        articlesQueueName: $articlesQueueName
        entityId: $entityId
        first: $first
        relatedSectionsQueueName: $relatedSectionsQueueName
      )
      @include(if: $isStyleSubSection)
  }
`;

const SubSectionPage: RelayComponent<unknown, subSectionPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const { trackCurrentItem } = useTrackCurrentItem();

  useEffect(() => {
    trackCurrentItem(data.subSection);
  }, [data.subSection, trackCurrentItem]);

  const { query: routeQuery } = useRouter();
  const handleRenderPageSeo = () => {
    switch (routeQuery.sectionVariant) {
      case Variant.Style:
        return <SectionStyleSeo reference={data.subSection} />;
      default:
        return <SectionSeo reference={data.subSection} twitter={{ cardType: "summary" }} />;
    }
  };

  const handleRenderSubSection = () => {
    switch (routeQuery.sectionVariant) {
      case Variant.Style:
        return (
          <SectionContextProvider reference={data.subSection}>
            <SubSectionStyle reference={data} />
          </SectionContextProvider>
        );
      default:
        return (
          <SectionContextProvider reference={data.subSection}>
            <SubSection reference={data} />
          </SectionContextProvider>
        );
    }
  };

  return (
    <>
      {handleRenderPageSeo()}
      {handleRenderSubSection()}
    </>
  );
};

SubSectionPage.displayName = "SubSectionPage";

const getPageProps = (context: NextPageContext) => {
  const isSectionStyle = context.query.sectionVariant === SectionVariant.Style;
  const pageProps: PageProps = {
    appBarConfiguration: {
      adSlotVariants: {
        large: {
          height: isSectionStyle ? 360 : 250,
          padding: isSectionStyle ? 24 : 20,
        },
        small: {
          height: 100,
          padding: 10,
        },
      },
      hasDesktopAd: true,
      hasMobileAd: true,
      variant: context.query.appBarVariant as AppBarVariant,
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 12,
        tabletUp: 0,
      },
    },
    pageConfiguration: {
      responsivePadding: {
        tabletUp: 24,
      },
    },
  };

  return pageProps;
};

export default withRelay(SubSectionPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    const parentSectionUuid = parseQueryString(context.query.parentSection) ?? "";
    const entityId = parseQueryString(context.query.entityId) ?? "";

    const queueNames = {
      articlesQueueName: `section_top_${entityId}`,
      commentQueueName: `section_top_opinion_${entityId}`,
      multimediaQueueName: `section_top_visual_stories_${entityId}`,
      relatedSectionsQueueName:
        parseQueryString(context.query.relatedSectionsQueueName) ??
        `section_categories_${entityId}`,
      relatedTopicsQueueName: `related_topics_${entityId}`,
      subSectionsQueueName: `section_categories_${entityId}`,
    };

    return {
      ...queueNames,
      customContents: DefaultCustomContents,
      entityId,
      first: 23,
      isStyleSubSection: context.query.sectionVariant === Variant.Style,
      parentSectionUuid,
      scmpPlusPaywallTypeIds: [scmpPlus?.article?.paywallTypeId],
    };
  },
});
